'use client';
import { usePathname } from 'next/navigation';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { APPLY_V3_URL_PREFIX } from '../../utils/routing';
import { ApplyCtaLink } from '../ApplyCta';
import { useAsideModalClose } from '../AsideModal';
import ProductCardButton from './ProductCardButton';

type ProductApplyCtaProps = {
  product: CmsProduct;
  ctaLinkOverride: string | undefined;
  pushDataLayer: PushDataLayerFn;
  /** Layout of the CTA's slot, which differs per viewport. */
  className?: string;
};

/**
 * The apply CTA of the product-details panel, shared by its two slots — the
 * sticky product card (desktop) and the bottom bar below it (mobile).
 *
 * Normally a link through `<ApplyCtaLink>`, so a desktop click on an
 * `/apply-v3/{id}` destination opens the apply-v3 modal instead of navigating
 * (CMS-1740).
 *
 * On the apply-v3 page the panel is opened from the form the user is already
 * filling in for this very product, so applying again leads nowhere: there the
 * CTA is a button that just returns them to the form (CMS-1954).
 *
 * That button keeps the apply copy, the open-in-new icon and the "on {provider}
 * website" subtext even though it only dismisses the panel — a product decision
 * on CMS-1954, taken knowing the control then describes an action it does not
 * perform. Two consequences follow from it: the CTA renders there even for a
 * product with no destination, where every other surface would render nothing,
 * and the route carries no ctrl-click / middle-click escape hatch, the control
 * no longer being a link.
 */
export const ProductApplyCta: React.FC<ProductApplyCtaProps> = ({
  product,
  ctaLinkOverride,
  pushDataLayer,
  className,
}) => {
  const closeAsideModal = useAsideModalClose();
  // Typed nullable by the consumer's Next version, never null in a client tree.
  const pathname = usePathname() ?? '';
  const isOnApplyPage = pathname.includes(`/${APPLY_V3_URL_PREFIX}/`);

  const button = (
    <ProductCardButton
      product={product}
      pushDataLayer={pushDataLayer}
      // Dismissing the panel is not an apply: it must not emit the apply event
      // or overwrite the session's recorded apply source.
      onClick={isOnApplyPage ? closeAsideModal : undefined}
    />
  );

  return (
    <div className={className} data-testid="product-apply-cta">
      {isOnApplyPage ? (
        button
      ) : (
        <ApplyCtaLink
          product={product}
          ctaLinkOverride={ctaLinkOverride}
          target="_blank"
          rel="noopener noreferrer"
        >
          {button}
        </ApplyCtaLink>
      )}
    </div>
  );
};

export default ProductApplyCta;
